<script setup>
import { ref, computed, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useNomeDoAgente } from 'dashboard/composables/useNomeDoAgente';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import sugestoesAPI from 'dashboard/api/captain/sugestoes';

// ══════════════════════════════════════════════════════════════════════════
// A SUGESTÃO DA ANDIE — modo assistido
// ══════════════════════════════════════════════════════════════════════════
//
// Em modo assistido, a Andie não fala com o lead: ela prepara a resposta, que
// chega aqui, em cima da caixa de resposta, para alguém do escritório enviar
// (do jeito que está ou editada) ou descartar. A resposta sai COMO ANDIE —
// em nome da pessoa, a trava "humano já respondeu" desligaria a Andie no meio
// da triagem. Ver Lumos::SugestaoDaAndie.
//
// A fonte é a nota privada da própria conversa: quando alguém decide, o
// servidor atualiza a nota e o tempo real do Chatwoot tira o cartão da tela
// para todo mundo — ninguém aprova a mesma sugestão duas vezes.
const props = defineProps({
  messages: { type: Array, default: () => [] },
});

const { t } = useI18n();
const { nomeDoAgente } = useNomeDoAgente();
const S = 'CAPTAIN.SUGESTAO';

// Decididas nesta tela: o cartão sai na hora, sem esperar o tempo real
// (que pode atrasar ou cair) trazer a nota atualizada.
const decididas = ref(new Set());

const pendente = computed(() =>
  [...props.messages]
    .reverse()
    .find(
      m =>
        m.private &&
        m.content_attributes?.lumos_sugestao?.estado === 'pendente' &&
        !decididas.value.has(m.id)
    )
);

const tirarDaTela = id => {
  decididas.value = new Set(decididas.value).add(id);
};

const dados = computed(
  () => pendente.value?.content_attributes?.lumos_sugestao
);
const texto = ref('');
const decidindo = ref('');

watch(
  () => pendente.value?.id,
  () => {
    texto.value = dados.value?.texto || '';
  },
  { immediate: true }
);

// A caixa cresce até o tamanho do texto (com teto): quem aprova lê a
// resposta inteira sem rolar dentro de uma caixinha de três linhas.
const ALTURA_MAXIMA = 240;
const caixa = ref(null);
const ajustarAltura = () =>
  nextTick(() => {
    const el = caixa.value;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight + 2, ALTURA_MAXIMA)}px`;
  });
watch([texto, caixa], ajustarAltura, { immediate: true });

const editada = computed(
  () => texto.value.trim() !== (dados.value?.texto || '').trim()
);
const quantasGravacoes = computed(() => (dados.value?.midias || []).length);

// F4.b (17/10/2026): a explicação de um andamento ao CLIENTE. Quem aprova vê de qual
// processo é e o texto do andamento que a IA leu — para conferir antes de enviar.
const ehExplicacao = computed(() => dados.value?.origem === 'explicador');
const andamentosLidos = computed(() => dados.value?.andamentos || []);

const decidir = async acao => {
  const { id } = pendente.value;
  decidindo.value = acao;
  try {
    if (acao === 'enviar') {
      await sugestoesAPI.enviar(id, editada.value ? texto.value : null);
      useAlert(t(`${S}.ENVIADA`));
    } else {
      await sugestoesAPI.descartar(id);
      useAlert(t(`${S}.DESCARTADA`));
    }
    tirarDaTela(id);
  } catch (erro) {
    // 409: outra pessoa decidiu primeiro — também sai da tela.
    const jaDecidida = erro?.response?.status === 409;
    if (jaDecidida) tirarDaTela(id);
    useAlert(t(`${S}.${jaDecidida ? 'JA_DECIDIDA' : 'ERRO'}`));
  } finally {
    decidindo.value = '';
  }
};
</script>

<template>
  <Transition name="advup-altura">
    <div
      v-if="pendente"
      class="mx-4 mb-2 flex flex-col gap-2 rounded-xl border border-n-iris-6 bg-n-iris-2 px-3 py-2.5"
    >
      <div class="flex items-center gap-2">
        <span class="i-ph-sparkle size-4 text-n-iris-11 flex-shrink-0" />
        <span class="text-sm font-medium text-n-slate-12 flex-1 min-w-0">
          {{
            ehExplicacao
              ? t(`${S}.EXPLICADOR_TITULO`, { processo: dados.processo })
              : t(`${S}.TITULO`, { agente: nomeDoAgente })
          }}
        </span>
        <span v-if="quantasGravacoes" class="text-xs text-n-slate-11">
          {{ t(`${S}.GRAVACOES`, { n: quantasGravacoes }) }}
        </span>
      </div>
      <details
        v-if="ehExplicacao && andamentosLidos.length"
        class="sug-andamentos text-xs text-n-slate-11"
      >
        <summary class="cursor-pointer select-none font-medium text-n-slate-11">
          {{ t(`${S}.EXPLICADOR_VER`, { n: andamentosLidos.length }) }}
        </summary>
        <ul class="mt-1.5 mb-0 ms-0 flex list-none flex-col gap-1.5 p-0">
          <li
            v-for="(a, i) in andamentosLidos"
            :key="i"
            class="rounded-md bg-n-solid-1 px-2 py-1.5 text-n-slate-12"
          >
            <span class="font-medium text-n-slate-11">{{ a.data }}</span>
            · {{ a.texto }}
          </li>
        </ul>
      </details>
      <textarea
        ref="caixa"
        v-model="texto"
        rows="3"
        class="w-full resize-none overflow-y-auto rounded-lg border border-n-weak bg-n-solid-1 px-2.5 py-2 text-sm text-n-slate-12 !mb-0"
        :aria-label="t(`${S}.TITULO`, { agente: nomeDoAgente })"
      />
      <div class="flex items-center gap-2">
        <span class="text-xs text-n-slate-10 flex-1">
          {{
            editada
              ? t(`${S}.VAI_EDITADA`, { agente: nomeDoAgente })
              : t(`${S}.COMO_ANDIE`, { agente: nomeDoAgente })
          }}
        </span>
        <Button
          :label="t(`${S}.DESCARTAR`)"
          variant="ghost"
          color="slate"
          size="sm"
          :is-loading="decidindo === 'descartar'"
          :disabled="!!decidindo"
          @click="decidir('descartar')"
        />
        <Button
          :label="editada ? t(`${S}.ENVIAR_EDITADA`) : t(`${S}.ENVIAR`)"
          size="sm"
          :is-loading="decidindo === 'enviar'"
          :disabled="!!decidindo || !texto.trim()"
          @click="decidir('enviar')"
        />
      </div>
    </div>
  </Transition>
</template>
